/**
 * Copyright (c) 2023 Gitpod GmbH. All rights reserved.
 * Licensed under the GNU Affero General Public License (AGPL).
 * See License.AGPL.txt in the project root for license information.
 */

import React from "react";
import * as ReactCheckbox from "@radix-ui/react-checkbox";
import { type PropsWithClassName, cn } from "@/components/podkit/lib/cn";
import { CheckIcon } from "lucide-react";

type CheckboxProps = React.ComponentPropsWithoutRef<typeof ReactCheckbox.Root> & PropsWithClassName;

export type CheckedState = ReactCheckbox.CheckedState;

export const Checkbox = React.forwardRef<React.ElementRef<typeof ReactCheckbox.Root>, CheckboxProps>(
    ({ className, ...props }, ref) => {
        return (
            <ReactCheckbox.Root
                className={cn(
                    "size-4",
                    "border-1 rounded",
                    "flex items-center justify-center",
                    "focus-visible:ring-kumquat-base/70 transition-opacity duration-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-0",
                    // checked state colors
                    "data-[state=checked]:bg-kumquat-600 data-[state=checked]:hover:opacity-80 data-[state=checked]:disabled:opacity-40",
                    // unchecked state colors
                    "data-[state=unchecked]:border data-[state=unchecked]:border-[#dadada] data-[state=unchecked]:bg-surface-pure data-[state=unchecked]:hover:bg-surface-secondary data-[state=unchecked]:disabled:bg-surface-secondary",
                    className,
                )}
                {...props}
                ref={ref}
            >
                <span className="flex size-3 items-center justify-center">
                    <ReactCheckbox.Indicator>
                        <CheckIcon className="pt-0.25 size-3 text-surface-primary" />
                    </ReactCheckbox.Indicator>
                </span>
            </ReactCheckbox.Root>
        );
    },
);
Checkbox.displayName = ReactCheckbox.Root.displayName;
